var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { UiButton } from '@claims-ia/design-system';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import RadioPrimaryButton from '@declaration/components/UI/RadioPrimaryButton.vue';
import FileCollectionUpload from '@declaration/components/upload/FileCollectionUpload.vue';
import { ChargeTypology } from '@shared/types/api/charge';
import { translateChoices } from '@shared/choice';
import { YES_OR_NO_CHOICES } from '@declaration/helpers/choices';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
import { ClaimFileType } from '@shared/types/file';
import { ApiEntityType } from '@shared/types/api/apiEntityType';
import { newClientIdentifier } from '@shared/utils/clientIdentifier';
import { Step } from '@shared/claim/steps';
const claim = namespace('claim');
const qualification = namespace('qualification');
const counting = namespace('counting');
const settings = namespace('settings');
let WaterLeakageCauseLocalizedPlumberIntervenedQuestion = class WaterLeakageCauseLocalizedPlumberIntervenedQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.ClaimFileType = ClaimFileType;
        this.hasPlumberIntervened = null;
        this.plumberInvoice = null;
        this.jointReport = null;
    }
    get selfCareSettings() {
        return this.setting('selfCareSettings');
    }
    get stepSettings() {
        var _a, _b, _c, _d;
        return ((_d = (_c = (_b = (_a = this.selfCareSettings) === null || _a === void 0 ? void 0 : _a.declaration) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c.waterLeakageCauseLocalizedPlumberIntervened) !== null && _d !== void 0 ? _d : {});
    }
    get panelTitle() {
        const tcKey = 'step.qualification/degat-des-eaux/fuite-localisee/plombier-intervenu.panel-title';
        return this.$tc(tcKey);
    }
    get sidebarTitle() {
        const tcKey = 'step.qualification/degat-des-eaux/fuite-localisee/plombier-intervenu.question-sidebar_title';
        return this.$te(tcKey) ? this.$tc(tcKey) : this.panelTitle;
    }
    get choices() {
        return translateChoices(YES_OR_NO_CHOICES, this.$i18n.tc.bind(this.$i18n));
    }
    get hideHelpLink() {
        return insurerHasFeature(InsurerFeatureFlag.HideHelpLink);
    }
    get showHelpLink() {
        if (this.stepSettings.showHelpLink)
            return true;
        return !insurerHasFeature(InsurerFeatureFlag.HideHelpLink);
    }
    get shouldAskForJointReport() {
        var _a;
        return (_a = this.stepSettings.askForJointReport) !== null && _a !== void 0 ? _a : false;
    }
    get shouldAskForPlumberInvoice() {
        var _a;
        return (_a = this.stepSettings.askForPlumberInvoice) !== null && _a !== void 0 ? _a : false;
    }
    get showPlumberInvoiceUpload() {
        return this.shouldAskForPlumberInvoice && this.hasPlumberIntervened;
    }
    get showJointReportUpload() {
        return this.shouldAskForJointReport && this.hasPlumberIntervened;
    }
    get showNextButton() {
        if (this.hasPlumberIntervened === null)
            return false;
        return this.showPlumberInvoiceUpload || this.showJointReportUpload;
    }
    get validationLabel() {
        return this.$tc('claim.damage.joint-report.next-step');
    }
    get answer() {
        if (this.qualification.hasPlumberIntervened === null) {
            return null;
        }
        const key = this.qualification.hasPlumberIntervened ? 'yes' : 'no';
        const tcKey = `step.qualification/degat-des-eaux/fuite-localisee/plombier-intervenu.answers.${key}`;
        return this.$tc(tcKey);
    }
    get isLoadingYesNoAnswer() {
        if (this.showPlumberInvoiceUpload || this.showJointReportUpload)
            return false;
        return this.isUpdatingClaim;
    }
    mounted() {
        var _a;
        this.hasPlumberIntervened = this.qualification.hasPlumberIntervened;
        this.jointReport = this.qualification.jointReport;
        const existingCharge = this.counting.charges.find((c) => c.typology === ChargeTypology.LeakageDetectionFees);
        if (existingCharge) {
            this.plumberInvoice = (_a = existingCharge.invoice) !== null && _a !== void 0 ? _a : null;
        }
    }
    async onHasPlumberIntervenedUpdate(newValue) {
        if (typeof newValue !== 'boolean' ||
            this.showPlumberInvoiceUpload ||
            this.showJointReportUpload) {
            return;
        }
        await this.validate();
    }
    async validate() {
        const qualificationPayload = {
            hasPlumberIntervened: this.hasPlumberIntervened,
        };
        if (this.shouldAskForJointReport &&
            this.jointReport &&
            this.jointReport.uploadedAppFiles.length > 0) {
            qualificationPayload.jointReport = this.jointReport;
        }
        await this.updateQualification({ qualificationPayload });
        const charges = this.updateCharges();
        const countingPayload = { charges };
        await this.updateCounting(countingPayload);
        const payload = {
            step: Step.WaterLeakageCauseLocalizedPlumberIntervenedWithDocuments,
            counting: {
                id: this.counting.id,
                ...countingPayload,
            },
            waterDamageQualification: {
                id: this.qualification.id,
                ...qualificationPayload,
            },
        };
        await this.saveClaimWithPayload(payload);
    }
    updateCharges() {
        const updatedCharges = this.counting.charges.map((c) => { var _a; return (_a = c.id) !== null && _a !== void 0 ? _a : c; });
        const existingCharge = this.counting.charges.find((c) => c.typology === ChargeTypology.LeakageDetectionFees);
        if (this.shouldAskForPlumberInvoice) {
            if (!existingCharge)
                return updatedCharges;
            return updatedCharges.filter((c) => {
                if (typeof c === 'string')
                    return c !== existingCharge.id;
                return c.id !== existingCharge.id;
            });
        }
        if (this.plumberInvoice && this.plumberInvoice.uploadedAppFiles.length > 0) {
            // Si on a une facture, on ajoute ou met à jour la charge de frais de détection de fuite
            if (existingCharge) {
                // Si la charge existe déjà, on la met à jour
                const existingIndex = this.counting.charges.indexOf(existingCharge);
                updatedCharges.splice(existingIndex, 1, {
                    id: existingCharge.id,
                    invoice: this.plumberInvoice,
                });
            }
            else {
                // Sinon, on ajoute une nouvelle charge
                updatedCharges.push({
                    '@type': ApiEntityType.Charge,
                    clientIdentifier: newClientIdentifier(),
                    typology: ChargeTypology.LeakageDetectionFees,
                    invoice: this.plumberInvoice,
                });
            }
        }
        else if (existingCharge) {
            // Si on n'a pas de facture mais qu'une charge existe, on la supprime
            const existingIndex = this.counting.charges.indexOf(existingCharge);
            if (existingIndex !== -1)
                updatedCharges.splice(existingIndex, 1);
        }
        return updatedCharges;
    }
};
__decorate([
    claim.State('isUpdatingClaim')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "isUpdatingClaim", void 0);
__decorate([
    qualification.State('qualification')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "qualification", void 0);
__decorate([
    counting.State('counting')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "counting", void 0);
__decorate([
    settings.Getter('setting')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "setting", void 0);
__decorate([
    qualification.Action('updateQualification')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "updateQualification", void 0);
__decorate([
    counting.Action('updateCounting')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "updateCounting", void 0);
__decorate([
    claim.Action('saveClaimWithPayload')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "saveClaimWithPayload", void 0);
__decorate([
    Watch('hasPlumberIntervened')
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion.prototype, "onHasPlumberIntervenedUpdate", null);
WaterLeakageCauseLocalizedPlumberIntervenedQuestion = __decorate([
    Component({
        components: { RadioPrimaryButton, QuestionPanel, FileCollectionUpload, UiButton },
        props: QuestionProps,
    })
], WaterLeakageCauseLocalizedPlumberIntervenedQuestion);
export default WaterLeakageCauseLocalizedPlumberIntervenedQuestion;
